import { useCallback, useEffect, useState } from "react";
import { Loader2, MousePointerClick, Users, Eye, Timer, TrendingUp, CarFront, Info } from "lucide-react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api } from "@/lib/api";

const EVENT_LABELS = {
  auftrag_gestartet: "Auftrag gestartet",
  auftrag_abgeschickt: "Auftrag abgeschickt",
  whatsapp_click: "WhatsApp",
  anruf_click: "Anrufen",
  email_click: "E-Mail",
  rent500_click: "Rent500-Link",
  rent500_code_kopiert: "Rent500-Code kopiert",
  highend_click: "High-End-Link",
  bezahlen_click: "Bezahlen",
};

const RANGES = [
  { id: "1", label: "Heute" },
  { id: "7", label: "7 Tage" },
  { id: "30", label: "30 Tage" },
  { id: "90", label: "90 Tage" },
  { id: "custom", label: "Individuell" },
];

const fmtDuration = (sec) => {
  if (!sec) return "–";
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m} Min. ${s} Sek.` : `${s} Sek.`;
};

function Kpi({ icon: Icon, label, value, sub, testId }) {
  return (
    <div className="border border-white/10 bg-white/[0.02] p-5" data-testid={testId}>
      <div className="flex items-center gap-2 text-zinc-500 mb-3">
        <Icon size={14} />
        <p className="text-[10px] uppercase tracking-[0.25em]">{label}</p>
      </div>
      <p className="font-display text-3xl text-bone">{value}</p>
      {sub && <p className="text-xs text-zinc-500 mt-2">{sub}</p>}
    </div>
  );
}

function DataList({ title, items, right, emptyText = "Noch keine Daten vorhanden", testId }) {
  return (
    <div className="border border-white/10 bg-white/[0.02] p-5" data-testid={testId}>
      <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-4">{title}</p>
      {!items || items.length === 0 ? (
        <p className="text-sm text-zinc-600">{emptyText}</p>
      ) : (
        <ul className="space-y-2.5">
          {items.map((it) => (
            <li key={it.label} className="flex items-center justify-between gap-4 text-sm">
              <span className="text-zinc-300 truncate">{it.label}</span>
              <span className="text-goldlight shrink-0 font-medium">{right ? right(it) : it.count}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function AdminAnalytics() {
  const [range, setRange] = useState("7");
  const [custom, setCustom] = useState({ start: "", end: "" });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params =
        range === "custom" && custom.start && custom.end
          ? { start: custom.start, end: custom.end }
          : { days: Number(range) };
      const { data } = await api.get("/analytics/stats", { params });
      setStats(data);
    } catch {
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [range, custom]);

  useEffect(() => {
    if (range !== "custom" || (custom.start && custom.end)) load();
  }, [load, range, custom]);

  const eventLabel = (name) => EVENT_LABELS[name] || name;

  return (
    <div data-testid="analytics-panel" className="space-y-6">
      <div className="flex flex-wrap gap-2" data-testid="analytics-range-selector">
        {RANGES.map((r) => (
          <button
            key={r.id}
            onClick={() => setRange(r.id)}
            data-testid={`analytics-range-${r.id}`}
            className={`px-4 py-2 text-[11px] uppercase tracking-[0.2em] border transition-colors duration-300 ${
              range === r.id ? "border-goldlight text-goldlight" : "border-white/10 text-zinc-500 hover:text-bone"
            }`}
          >
            {r.label}
          </button>
        ))}
        {range === "custom" && (
          <span className="flex items-center gap-2">
            <input
              type="date"
              value={custom.start}
              onChange={(e) => setCustom((c) => ({ ...c, start: e.target.value }))}
              data-testid="analytics-custom-start"
              className="bg-transparent border border-white/15 px-3 py-2 text-sm text-bone [color-scheme:dark]"
            />
            <span className="text-zinc-500">–</span>
            <input
              type="date"
              value={custom.end}
              onChange={(e) => setCustom((c) => ({ ...c, end: e.target.value }))}
              data-testid="analytics-custom-end"
              className="bg-transparent border border-white/15 px-3 py-2 text-sm text-bone [color-scheme:dark]"
            />
          </span>
        )}
      </div>

      {loading || !stats ? (
        <div className="flex items-center gap-3 text-zinc-500 py-20 justify-center">
          <Loader2 size={18} className="animate-spin" /> Lade Statistiken ...
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi icon={Users} label="Besucher" value={stats.visitors} sub={`Wiederkehrend (mit Einwilligung): ${stats.returning_consented}`} testId="analytics-kpi-visitors" />
            <Kpi icon={Eye} label="Seitenaufrufe" value={stats.pageviews} sub={`Absprungrate: ${stats.bounce_rate} %`} testId="analytics-kpi-pageviews" />
            <Kpi icon={Timer} label="Ø Aufenthalt" value={fmtDuration(stats.avg_duration_sec)} sub="bei Sitzungen mit mehreren Seiten" testId="analytics-kpi-duration" />
            <Kpi
              icon={TrendingUp}
              label="Auftrags-Conversion"
              value={`${stats.funnel.conversion_rate} %`}
              sub={`${stats.funnel.auftrag_gestartet} gestartet · ${stats.funnel.auftrag_abgeschickt} abgeschickt`}
              testId="analytics-kpi-conversion"
            />
          </div>

          <div className="border border-white/10 bg-white/[0.02] p-5" data-testid="analytics-chart">
            <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-4">Verlauf — Besucher & Seitenaufrufe</p>
            {stats.series.every((d) => d.pageviews === 0) ? (
              <p className="text-sm text-zinc-600 py-10 text-center">Noch keine Daten im gewählten Zeitraum</p>
            ) : (
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={stats.series} margin={{ top: 5, right: 5, left: -25, bottom: 0 }}>
                    <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                    <XAxis dataKey="day" tick={{ fill: "#71717a", fontSize: 10 }} tickFormatter={(d) => d.slice(8) + "." + d.slice(5, 7)} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: "#71717a", fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ background: "#18181b", border: "1px solid rgba(255,255,255,0.1)", fontSize: 12 }}
                      labelStyle={{ color: "#e4e4e7" }}
                    />
                    <Area type="monotone" dataKey="pageviews" name="Seitenaufrufe" stroke="#d4af37" fill="#d4af37" fillOpacity={0.12} strokeWidth={1.5} />
                    <Area type="monotone" dataKey="visitors" name="Besucher" stroke="#7dd3fc" fill="#7dd3fc" fillOpacity={0.08} strokeWidth={1.5} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          <div className="border border-goldlight/30 bg-goldlight/5 p-5" data-testid="analytics-rent500-panel">
            <div className="flex items-center gap-2 mb-4">
              <CarFront size={16} className="text-goldlight" />
              <p className="text-[10px] uppercase tracking-[0.25em] text-goldlight">Rent500-Partnertracking</p>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <div>
                <p className="text-2xl font-display text-bone" data-testid="rent500-clicks-total">{stats.rent500.clicks}</p>
                <p className="text-xs text-zinc-500 mt-1">Klicks auf den Partnerlink</p>
              </div>
              <div>
                <p className="text-2xl font-display text-bone" data-testid="rent500-unique">{stats.rent500.unique_clickers}</p>
                <p className="text-xs text-zinc-500 mt-1">Weitergeleitete Besucher</p>
              </div>
              <div>
                <p className="text-2xl font-display text-zinc-500" data-testid="rent500-conversions">nicht verfügbar</p>
                <p className="text-xs text-zinc-500 mt-1">Bestätigte Buchungen</p>
              </div>
              <div>
                <p className="text-2xl font-display text-zinc-500" data-testid="rent500-commission">nicht bestätigt</p>
                <p className="text-xs text-zinc-500 mt-1">Provision</p>
              </div>
            </div>
            <p className="text-xs text-zinc-400 leading-relaxed flex items-start gap-2">
              <Info size={14} className="shrink-0 mt-0.5 text-zinc-500" />
              <span>
                Jeder Klick läuft über einen getrackten Link mit UTM-Kennung (sichtbar in der Rent500-Website-Statistik).
                Rent500 bietet derzeit keine API/Webhooks — Buchungen und Provision können daher nicht automatisch bestätigt
                werden. Frage bei Rent500 regelmäßig nach Abrechnungsberichten zu deinem Code JUSTIN.
              </span>
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-3">
            <DataList title="Meistgesehene Bereiche" testId="analytics-sections" items={stats.top_sections} right={(it) => `${it.share} %`} />
            <DataList
              title="Meistgeklickte Aktionen"
              testId="analytics-events"
              items={stats.top_events.map((e) => ({ ...e, label: eventLabel(e.label) }))}
            />
            <DataList title="Top-Seiten" testId="analytics-pages" items={stats.top_pages} />
            <DataList title="Geräte" testId="analytics-devices" items={stats.devices} />
            <DataList title="Herkunft (Verweis-Seiten)" testId="analytics-referrers" items={stats.referrers} emptyText="Noch keine Verweise — Besucher kommen direkt" />
            <DataList title="Einstiegsseiten" testId="analytics-entries" items={stats.entry_pages} />
            <DataList title="Ausstiegsseiten" testId="analytics-exits" items={stats.exit_pages} />
            <div className="border border-white/10 bg-white/[0.02] p-5" data-testid="analytics-privacy-note">
              <div className="flex items-center gap-2 text-zinc-500 mb-3">
                <MousePointerClick size={14} />
                <p className="text-[10px] uppercase tracking-[0.25em]">Datenschutz</p>
              </div>
              <p className="text-xs text-zinc-400 leading-relaxed">
                Es werden keine IP-Adressen gespeichert und keine Cookies ohne Einwilligung gesetzt.
                Tages-Sitzungen nutzen einen nicht umkehrbaren Hash. „Neu vs. wiederkehrend" ist nur für
                Besucher messbar, die Analysen aktiv erlaubt haben. Keine personenbezogenen Profile.
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
